import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PlaySolidIcon, PauseIcon } from "@fluentui/react-icons-mdl2";
import { Enums } from "@cornerstonejs/core";
import Spinner from "../../../../components/Spinner/Spinner";
import {
  getEngine,
  listEngines,
  subscribeEngineRegistry,
} from "../../helpers/MultiFramePlaybackEngine";
import {
  MULTI_FRAME_FPS_MIN,
  MULTI_FRAME_FPS_MAX,
} from "../../helpers/MultiFrameService";
import { getAutoHideViewportToolbar } from "../../helpers/multiFrameSettings";
import {
  SYNC_MODES,
  cycleSyncMode,
  getSyncModeLabel,
  subscribeSyncMode,
} from "../../helpers/MultiFramePlaybackBus";

const FPS_STEP = 1;
const FPS_STEP_FAST = 5;
const AUTO_HIDE_DELAY_MS = 2000;

// YouTube-style buffer gradient: played (bright blue) | buffered (dim blue) |
// unloaded (gray). When fully loaded the whole track stays blue (no gray).
// Computed from a snapshot so both the React render path and the imperative
// live-update path produce an identical background string.
const computeTimelineBackground = (snapshot) => {
  const playedPct =
    snapshot.totalFrames > 1
      ? (snapshot.currentFrame / (snapshot.totalFrames - 1)) * 100
      : 100;
  const bufferPct = Math.min(
    100,
    Math.max(
      playedPct,
      snapshot.totalFrames > 0
        ? (snapshot.loadedFrameCount / snapshot.totalFrames) * 100
        : 100
    )
  );
  return `linear-gradient(to right, #0078D4 ${playedPct}%, rgba(76,194,255,0.75) ${playedPct}% ${bufferPct}%, rgba(255,255,255,0.18) ${bufferPct}% 100%)`;
};

// Write the two high-frequency UI nodes directly. React renders NO children
// for the counter span, so this is the sole owner of its text — that avoids
// the reconciliation clash (React re-inserting a stale "1 of N" over the
// live-updating node) that happens when both React and an imperative write
// target the same node's children.
const applyHotDom = (counterEl, timelineEl, snap) => {
  if (!snap) return;
  if (counterEl) {
    counterEl.textContent = `${snap.currentFrame + 1} of ${snap.totalFrames}`;
  }
  if (timelineEl) {
    timelineEl.value = String(snap.currentFrame);
    timelineEl.style.background = computeTimelineBackground(snap);
  }
};

const MultiFrameToolbar = ({ viewportIndex }) => {
  const [engine, setEngine] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [syncMode, setSyncMode] = useState(SYNC_MODES.UNSYNCHRONIZED);
  const [activeEngineCount, setActiveEngineCount] = useState(
    () => listEngines().length
  );
  const [isViewportFrameLoading, setIsViewportFrameLoading] = useState(false);
  const hideTimerRef = useRef(null);
  // Refs to the two high-frequency UI nodes (frame counter + scrubber). On
  // mobile/iOS the CPU-rendered cine rAF loop saturates the main thread, so
  // React's setState-driven re-renders get starved and the counter/timeline
  // freeze even though the canvas keeps advancing. We write these two nodes
  // imperatively from the engine subscription so they stay in lockstep with
  // the frames regardless of whether React manages to flush a re-render.
  const counterRef = useRef(null);
  const timelineRef = useRef(null);

  // Watch global sync mode for the toggle's display state.
  useEffect(() => subscribeSyncMode(setSyncMode), []);

  // Recount active engines whenever the registry changes.
  useEffect(() => {
    const refresh = () => setActiveEngineCount(listEngines().length);
    refresh();
    return subscribeEngineRegistry(refresh);
  }, []);

  // Auto-hide toolbar after inactivity.
  useEffect(() => {
    if (!engine) return undefined;
    if (!getAutoHideViewportToolbar()) {
      setHidden(false);
      return undefined;
    }

    const viewportEl = document.getElementById("viewport_" + viewportIndex);
    if (!viewportEl) return undefined;

    const showAndArmTimer = () => {
      setHidden(false);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        setHidden(true);
      }, AUTO_HIDE_DELAY_MS);
    };

    showAndArmTimer();
    viewportEl.addEventListener("mousemove", showAndArmTimer);
    viewportEl.addEventListener("focusin", showAndArmTimer);

    return () => {
      viewportEl.removeEventListener("mousemove", showAndArmTimer);
      viewportEl.removeEventListener("focusin", showAndArmTimer);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [engine, viewportIndex]);

  // Attach / detach the playback engine for this viewport.
  useEffect(() => {
    const id = "viewport_" + viewportIndex;
    let snapshotUnsub = null;

    const attach = (nextEngine) => {
      if (snapshotUnsub) {
        snapshotUnsub();
        snapshotUnsub = null;
      }
      setEngine(nextEngine || null);
      if (nextEngine) {
        snapshotUnsub = nextEngine.subscribe((snap) => {
          // Hot-path: write counter + scrubber directly. This runs
          // synchronously inside the engine's rAF emit, so it survives the
          // React render starvation that freezes these nodes during
          // CPU-rendered cine on iOS.
          applyHotDom(counterRef.current, timelineRef.current, snap);
          setSnapshot(snap);
        });
      } else {
        setSnapshot(null);
      }
    };

    attach(getEngine(id));

    const unsubRegistry = subscribeEngineRegistry((vpId, e) => {
      if (vpId === id) attach(e);
    });

    return () => {
      if (snapshotUnsub) snapshotUnsub();
      unsubRegistry();
    };
  }, [viewportIndex]);

  // Seed / re-sync the counter + scrubber on every React commit. Since the
  // counter span renders no children, React never writes its text — this
  // layout effect (runs before paint) handles the initial value and any
  // structural re-render; the engine subscription handles the per-frame
  // updates that React's starved render loop misses on iOS.
  useLayoutEffect(() => {
    applyHotDom(counterRef.current, timelineRef.current, snapshot);
  }, [snapshot]);

  // Mark every PAINTED frame as loaded. This is a single persistent listener
  // (not re-attached per frame) so it can't miss an IMAGE_RENDERED that fires
  // before a per-frame listener attaches. A frame that's on screen is, by
  // definition, available — recording it here:
  //   (1) grows loadedFrameCount as the cine plays, so the buffer bar keeps
  //       filling past the initial warmup instead of stalling at it; and
  //   (2) guarantees the frame you pause on is known-loaded, so the demand-load
  //       overlay below never sticks waiting on a render that already happened.
  useEffect(() => {
    if (!engine) return undefined;
    const outerEl = document.getElementById("viewport_" + viewportIndex);
    const viewportEl = outerEl ? outerEl.querySelector("#viewportEntry") : null;
    if (!viewportEl) return undefined;
    const onRendered = () => engine.notifyFrameLoaded(engine.currentFrame);
    viewportEl.addEventListener(Enums.Events.IMAGE_RENDERED, onRendered);
    return () =>
      viewportEl.removeEventListener(Enums.Events.IMAGE_RENDERED, onRendered);
  }, [engine, viewportIndex]);

  // Demand-load overlay: shown ONLY when paused on a frame that isn't loaded
  // yet (a seek ahead of the prefetch). Never during playback (the engine
  // skip-and-continues; a per-frame loader would just flash). The persistent
  // listener above clears it by marking the frame loaded once it paints — so
  // it resolves both when a seeked frame finishes demand-loading and when the
  // paused frame was already on screen.
  useEffect(() => {
    const shouldShow =
      !!engine &&
      !!snapshot &&
      !snapshot.isPlaying &&
      !snapshot.isCurrentFrameLoaded;
    setIsViewportFrameLoading(shouldShow);
  }, [engine, snapshot?.isPlaying, snapshot?.isCurrentFrameLoaded]);

  if (!engine || !snapshot || snapshot.totalFrames < 2) {
    return null;
  }

  const handlePlayPause = () => engine.togglePlay();
  const handlePrev = () => engine.step(-1);
  const handleNext = () => engine.step(+1);

  const clampFps = (fps) =>
    Math.max(MULTI_FRAME_FPS_MIN, Math.min(MULTI_FRAME_FPS_MAX, fps));

  const handleFpsDown = (event) => {
    const delta = event.shiftKey ? FPS_STEP_FAST : FPS_STEP;
    engine.setFps(clampFps(Math.round(snapshot.fps) - delta));
  };

  const handleFpsUp = (event) => {
    const delta = event.shiftKey ? FPS_STEP_FAST : FPS_STEP;
    engine.setFps(clampFps(Math.round(snapshot.fps) + delta));
  };

  const handleTimelineChange = (event) => {
    if (engine.isPlaying) engine.pause();
    const next = parseInt(event.target.value, 10);
    if (Number.isFinite(next)) engine.seekTo(next);
  };

  const handleTimelineWheel = (event) => {
    if (!engine) return;
    event.preventDefault();
    event.stopPropagation();
    if (engine.isPlaying) engine.pause();
    engine.step(event.deltaY > 0 ? +1 : -1);
  };

  const timelineStyle = { background: computeTimelineBackground(snapshot) };

  return (
    <>
      {isViewportFrameLoading && (
        <div
          className="multi-frame-viewport-loading-overlay"
          aria-hidden="true"
        >
          <Spinner size={12} />
        </div>
      )}
      <div
        className={`multi-frame-toolbar ${hidden ? "multi-frame-toolbar-hidden" : ""}`}
        role="toolbar"
        aria-label="Multi-frame playback controls"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {/* Row 1: timeline — pulled to top via CSS order:-1 */}
        <input
          ref={timelineRef}
          type="range"
          className="multi-frame-toolbar-timeline"
          style={timelineStyle}
          min={0}
          max={snapshot.totalFrames - 1}
          step={1}
          value={snapshot.currentFrame}
          onChange={handleTimelineChange}
          onWheel={handleTimelineWheel}
          aria-label="Seek frame"
          aria-valuetext={`Frame ${snapshot.currentFrame + 1} of ${snapshot.totalFrames}`}
        />

        {/* Row 2 left: play / prev / next */}
        <div className="multi-frame-toolbar-group-left">
          <button
            type="button"
            className="multi-frame-toolbar-btn"
            title={snapshot.isPlaying ? "Pause (Space)" : "Play (Space)"}
            aria-label={snapshot.isPlaying ? "Pause playback" : "Play"}
            aria-pressed={snapshot.isPlaying}
            onClick={handlePlayPause}
          >
            {snapshot.isPlaying ? <PauseIcon /> : <PlaySolidIcon />}
          </button>
          <button
            type="button"
            className="multi-frame-toolbar-btn multi-frame-toolbar-step-btn"
            title="Previous frame (←)"
            aria-label="Previous frame"
            onClick={handlePrev}
          >
            <span aria-hidden="true">◀</span>
          </button>
          <button
            type="button"
            className="multi-frame-toolbar-btn multi-frame-toolbar-step-btn"
            title="Next frame (→)"
            aria-label="Next frame"
            onClick={handleNext}
          >
            <span aria-hidden="true">▶</span>
          </button>
        </div>

        {/* Row 2 center: frame counter. Intentionally has NO React children —
            its text is owned entirely by applyHotDom (see the layout effect
            and engine subscription) to avoid React/imperative reconciliation
            clashes on iOS. */}
        <span ref={counterRef} className="multi-frame-toolbar-counter" />

        {/* Row 2 right: sync (optional) + fps */}
        <div className="multi-frame-toolbar-group-right">
          {activeEngineCount > 1 && (
            <>
              <button
                type="button"
                className={
                  "multi-frame-toolbar-btn multi-frame-toolbar-sync-btn " +
                  (syncMode !== SYNC_MODES.UNSYNCHRONIZED
                    ? "multi-frame-toolbar-sync-active"
                    : "")
                }
                title={`Sync mode: ${getSyncModeLabel(syncMode)} — click to cycle`}
                aria-label={`Sync mode: ${getSyncModeLabel(syncMode)}. Click to cycle.`}
                onClick={() => cycleSyncMode()}
              >
                <span aria-hidden="true">
                  {syncMode === SYNC_MODES.UNSYNCHRONIZED
                    ? "⇌"
                    : syncMode === SYNC_MODES.PLAY_SYNC_START
                    ? "⇉"
                    : "⇶"}
                </span>
              </button>
              <span className="multi-frame-toolbar-divider" aria-hidden="true" />
            </>
          )}
          <button
            type="button"
            className="multi-frame-toolbar-btn multi-frame-toolbar-fps-btn"
            title="Decrease FPS (Shift+click for −5)"
            aria-label="Decrease FPS"
            onClick={handleFpsDown}
            disabled={Math.round(snapshot.fps) <= MULTI_FRAME_FPS_MIN}
          >
            −
          </button>
          <span className="multi-frame-toolbar-fps">
            {Math.round(snapshot.fps)} fps
          </span>
          <button
            type="button"
            className="multi-frame-toolbar-btn multi-frame-toolbar-fps-btn"
            title="Increase FPS (Shift+click for +5)"
            aria-label="Increase FPS"
            onClick={handleFpsUp}
            disabled={Math.round(snapshot.fps) >= MULTI_FRAME_FPS_MAX}
          >
            +
          </button>
        </div>
      </div>
    </>
  );
};

export default MultiFrameToolbar;
